import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Building2, ArrowRight, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { INDUSTRIES } from "@/lib/industries";

/**
 * Public client-onboarding page. Creates an account owned by the platform's
 * super-admin. An admin can later re-assign the client to an accountant.
 */
export default function Onboard() {
  const [form, setForm] = useState({
    business_name: "",
    name: "",
    email: "",
    phone: "",
    industry: "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {email, business_name}

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (!payload[k]) delete payload[k]; });
      await api.post("/onboarding/register-client", payload);
      setSubmitted({ email: form.email, business_name: form.business_name });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-4 py-10" data-testid="onboard-pending-page">
        <div className="w-full max-w-md card-surface p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-forest/10 text-forest flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <div className="overline mb-1">Signup received</div>
          <h1 className="font-heading text-2xl font-medium leading-tight">
            Thanks — we&apos;re reviewing your account.
          </h1>
          <p className="text-sm text-ink-light mt-3 leading-relaxed">
            You&apos;ll receive an email at <b>{submitted.email}</b> once <b>{submitted.business_name}</b> is approved and matched with an accountant. Usually within a business day.
          </p>
          <div className="mt-6">
            <Link
              to="/"
              className="text-sm text-forest hover:underline"
              data-testid="onb-back-home"
            >
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4 py-10" data-testid="onboard-page">
      <div className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left: pitch */}
        <div className="hidden md:flex flex-col justify-center pr-6">
          <div className="overline text-forest mb-2">Get started</div>
          <h1 className="font-heading text-4xl font-medium leading-tight">
            Send your bank statements once.<br />
            Get your books, taxes, and cash flow figured out.
          </h1>
          <p className="text-ink-light mt-4 leading-relaxed">
            Create your BizCashflow Pros account now. Upload your first statement in under a minute. We&apos;ll match you with an accountant from our network — or you can bring your own.
          </p>
          <ul className="mt-6 space-y-2.5 text-sm">
            {[
              "Bank statement upload (PDF, CSV — we OCR the rest)",
              "AI-parsed transactions ready for review",
              "Direct chat with your accountant",
              "Simple, one flat monthly fee",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-forest shrink-0 mt-0.5" strokeWidth={2} />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 text-xs text-ink-lighter">
            Already have an account? <Link to="/login" className="text-forest hover:underline">Sign in →</Link>
          </div>
        </div>

        {/* Right: form */}
        <div className="card-surface p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-forest text-white flex items-center justify-center">
              <Building2 className="w-4 h-4" strokeWidth={2} />
            </div>
            <div className="font-heading text-xl font-medium">Create your account</div>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <label className="block text-sm">
              <span className="font-medium">Business name</span>
              <input
                required
                autoFocus
                value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
                placeholder="Acme Trading LLC"
                data-testid="onb-business"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium">Industry</span>
              <select
                required
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
                data-testid="onb-industry"
              >
                <option value="">Pick your industry…</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="font-medium">Your name</span>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
                placeholder="Jane Doe"
                data-testid="onb-name"
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="font-medium">Email</span>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
                  data-testid="onb-email"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium">Phone <span className="text-ink-lighter font-normal">(optional)</span></span>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
                  placeholder="(555) 555-1234"
                  data-testid="onb-phone"
                />
              </label>
            </div>
            <label className="block text-sm">
              <span className="font-medium">Password</span>
              <input
                required
                type="password"
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
                placeholder="At least 6 characters"
                data-testid="onb-password"
              />
            </label>
            <button
              type="submit"
              disabled={submitting}
              className="btn-forest w-full inline-flex items-center justify-center gap-2 mt-2"
              data-testid="onb-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Create account
            </button>
            <p className="text-[11px] text-ink-lighter text-center mt-2 leading-relaxed">
              By continuing you agree to our terms. Your data is private and never shared with third parties.
            </p>
            <div className="md:hidden text-xs text-center text-ink-lighter mt-2">
              Already have an account? <Link to="/login" className="text-forest hover:underline">Sign in</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
